/**
 * BentoGrid - Modern 2026 dashboard layout system
 *
 * Features:
 * - Asymmetric grid layouts
 * - Responsive breakpoints
 * - Glass morphism effects
 * - Smooth hover animations
 * - Flexible slot system
 *
 * Based on modern dashboard design trends
 */

import React from 'react';

/**
 * BentoGrid - Container for bento-style layouts
 */
export default function BentoGrid({
  children,
  cols = 4,
  gap = 4,
  className = '',
}) {
  return (
    <div
      className={`grid gap-${gap} ${className}`}
      style={{
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
      }}
    >
      {children}
    </div>
  );
}

/**
 * BentoCard - Individual card in the bento grid
 */
export function BentoCard({
  children,
  colSpan = 1,
  rowSpan = 1,
  variant = 'default',
  hover = true,
  glass = false,
  padding = 'default',
  className = '',
  onClick,
  header,
  footer,
  icon,
  badge,
}) {
  const variants = {
    default: 'bg-zinc-800/50 border-zinc-700',
    elevated: 'bg-zinc-800/70 border-zinc-600 shadow-elevated',
    accent: 'bg-orange-500/10 border-orange-500/30',
    success: 'bg-green-500/10 border-green-500/30',
    warning: 'bg-yellow-500/10 border-yellow-500/30',
    danger: 'bg-red-500/10 border-red-500/30',
    gradient: 'bg-gradient-to-br from-zinc-800/80 to-zinc-900/80 border-zinc-700',
  };

  const paddings = {
    none: '',
    sm: 'p-3',
    default: 'p-4',
    lg: 'p-6',
  };

  const baseStyles = `
    relative border rounded-xl overflow-hidden
    transition-all duration-300 ease-out
    ${variants[variant] || variants.default}
    ${paddings[padding] || paddings.default}
    ${hover ? 'hover:border-zinc-600 hover:shadow-card-hover hover:-translate-y-0.5' : ''}
    ${glass ? 'backdrop-blur-sm' : ''}
    ${onClick ? 'cursor-pointer' : ''}
  `;

  return (
    <div
      className={`${baseStyles} ${className}`}
      style={{
        gridColumn: `span ${colSpan}`,
        gridRow: `span ${rowSpan}`,
      }}
      onClick={onClick}
    >
      {/* Header */}
      {header && (
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {icon && (
              <div className="w-8 h-8 rounded-lg bg-zinc-700/50 flex items-center justify-center">
                {icon}
              </div>
            )}
            <div>
              {typeof header === 'string' ? (
                <h3 className="text-base font-semibold text-white">{header}</h3>
              ) : (
                header
              )}
            </div>
          </div>
          {badge && (
            <span className="px-2 py-0.5 text-xs font-medium bg-zinc-700 text-zinc-300 rounded-full">
              {badge}
            </span>
          )}
        </div>
      )}

      {/* Content */}
      {children}

      {/* Footer */}
      {footer && (
        <div className="mt-4 pt-4 border-t border-zinc-700/50">
          {footer}
        </div>
      )}
    </div>
  );
}

/**
 * BentoStat - Compact stat display for bento cards
 */
export function BentoStat({
  label,
  value,
  subValue,
  trend,
  trendDirection,
  valueColor,
  size = 'md',
}) {
  const sizes = {
    sm: { value: 'text-xl', label: 'text-[10px]', subValue: 'text-[9px]' },
    md: { value: 'text-2xl', label: 'text-xs', subValue: 'text-[10px]' },
    lg: { value: 'text-3xl', label: 'text-sm', subValue: 'text-xs' },
  };

  const config = sizes[size] || sizes.md;

  return (
    <div className="flex flex-col">
      <span className={`text-zinc-500 uppercase tracking-wider ${config.label}`}>
        {label}
      </span>
      <div className="flex items-baseline gap-2">
        <span
          className={`font-bold font-mono ${config.value}`}
          style={{ color: valueColor || 'white' }}
        >
          {value}
        </span>
        {trend !== undefined && (
          <span
            className={`text-xs font-medium ${
              trendDirection === 'up' ? 'text-green-400' : 'text-red-400'
            }`}
          >
            {trendDirection === 'up' ? '+' : '-'}{trend}%
          </span>
        )}
      </div>
      {subValue && (
        <span className={`text-zinc-500 ${config.subValue}`}>{subValue}</span>
      )}
    </div>
  );
}

/**
 * BentoHeader - Section header for bento layouts
 */
export function BentoHeader({
  title,
  subtitle,
  icon,
  action,
  className = '',
}) {
  return (
    <div className={`flex items-center justify-between mb-6 ${className}`}>
      <div className="flex items-center gap-3">
        {icon && (
          <div className="w-10 h-10 rounded-xl bg-zinc-800 flex items-center justify-center">
            {icon}
          </div>
        )}
        <div>
          <h2 className="text-xl font-bold text-white">{title}</h2>
          {subtitle && <p className="text-sm text-zinc-500">{subtitle}</p>}
        </div>
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

/**
 * ResponsiveBentoGrid - Bento grid with responsive column adjustments
 */
export function ResponsiveBentoGrid({
  children,
  className = '',
}) {
  return (
    <div
      className={`
        grid gap-4
        grid-cols-1
        sm:grid-cols-2
        lg:grid-cols-4
        ${className}
      `}
    >
      {children}
    </div>
  );
}

/**
 * BentoDashboard - Full dashboard layout with sections
 */
export function BentoDashboard({ children, className = '' }) {
  return (
    <div className={`space-y-6 ${className}`}>
      {children}
    </div>
  );
}

/**
 * BentoSection - Section wrapper with title
 */
export function BentoSection({
  title,
  subtitle,
  icon,
  action,
  children,
  className = '',
}) {
  return (
    <section className={className}>
      {title && (
        <BentoHeader
          title={title}
          subtitle={subtitle}
          icon={icon}
          action={action}
        />
      )}
      {children}
    </section>
  );
}

/**
 * GlassCard - Card with glass morphism effect
 */
export function GlassCard({
  children,
  className = '',
  blur = 'md',
  opacity = 50,
}) {
  const blurs = {
    sm: 'backdrop-blur-sm',
    md: 'backdrop-blur-md',
    lg: 'backdrop-blur-lg',
    xl: 'backdrop-blur-xl',
  };

  return (
    <div
      className={`
        relative overflow-hidden rounded-xl
        border border-white/10
        ${blurs[blur] || blurs.md}
        ${className}
      `}
      style={{
        backgroundColor: `rgba(39, 39, 42, ${opacity / 100})`,
      }}
    >
      {children}
    </div>
  );
}

/**
 * StatRow - Horizontal stat display
 */
export function StatRow({ items, className = '' }) {
  return (
    <div className={`flex items-center divide-x divide-zinc-700 ${className}`}>
      {items.map((item, idx) => (
        <div key={idx} className="flex-1 px-4 first:pl-0 last:pr-0">
          <BentoStat {...item} />
        </div>
      ))}
    </div>
  );
}

/**
 * QuickActions - Action buttons row
 */
export function QuickActions({ actions, className = '' }) {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {actions.map((action, idx) => (
        <button
          key={idx}
          onClick={action.onClick}
          className={`
            flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium
            transition-all duration-200
            ${action.variant === 'primary'
              ? 'bg-orange-500 text-white hover:bg-orange-600'
              : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700 hover:text-white'
            }
          `}
        >
          {action.icon}
          {action.label}
        </button>
      ))}
    </div>
  );
}
